import {useFocusManagement} from '@aboutyou/a11y';
import {Fade, TransitionDuration} from '@aboutyou/transitions';
import {styled} from '@linaria/react';
import React, {useRef, useState} from 'react';
import {useDimensionsEffect} from 'react-viewport-utils';
import {COLORS} from '../../../constants/colors';
import {RADIUS, SPACING} from '../../../constants/sizing';
import {useKeyPress} from '../../../hooks/useKeyPress/useKeyPress';
import {HandleClickOutside} from '../../../hooks/useOnClickOutside/HandleClickOutside';
import {useOnClickOutsideMultipleTargets} from '../../../hooks/useOnClickOutside/useOnClickOutside';
import {ContentOpener, OpenerType} from '../ContentPopper/ContentOpener';
import {ContentPopperBaseProps} from '../ContentPopper/ContentPopper';
import {Divider} from '../Divider/Divider';
import {RenderInPortal} from '../RenderInPortal/RenderInPortal';

export type DropDownProps = ContentPopperBaseProps & {
  openerType?: OpenerType;
  closeOnContentClick?: boolean;
  bodyElevation?: number;
  bodyMarginTop?: number;
  fullWidthBody?: boolean;
  dropdownBodyAlign?: DropdownBodyAlignment;
  animationDuration?: TransitionDuration;
  dropdownBodyWidth?: string;
  setConstrainedHeight?: boolean;
  centerDropDownContent?: boolean;
  forceBlackBorderOnHover?: boolean;
  forceContentOpenerGreyBorder?: boolean;
  forceContentOpenerIconColor?: keyof typeof COLORS;
  disableClickOutside?: boolean;
  disableFocusOnOpenerButton?: boolean;
};

export const Dropdown = ({
  'data-testid': dataTestId,
  className,
  open,
  isActive = false,
  closeOnContentClick = false,
  openerType = 'single-arrow',
  bodyElevation = 99,
  bodyMarginTop = 5,
  fullWidthBody = false,
  openerContent,
  headerContent,
  bodyContent,
  onClose,
  onOpenerClick,
  dropdownBodyAlign: dropdownBodyAlignProp,
  animationDuration = 200,
  dropdownBodyWidth,
  setConstrainedHeight,
  centerDropDownContent,
  forceContentOpenerGreyBorder,
  forceContentOpenerIconColor,
  forceBlackBorderOnHover,
  renderInPortal,
  disableClickOutside,
  disableFocusOnOpenerButton,
  popoverPriority,
  ref,
}: DropDownProps & {ref?: React.Ref<HTMLDivElement>}) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const wrapperRef =
    (ref as React.RefObject<HTMLDivElement | null>) ?? innerRef;
  const dropdownOpenerRef = useRef<HTMLButtonElement>(null);
  const bodyWrapperRef = useRef<HTMLDivElement>(null);

  const {isTopMostPopover, focusManagementRefCallback} = useFocusManagement({
    isOpen: open,
    priority: popoverPriority,
  });
  useKeyPress({
    key: 'Escape',
    onKeyDown: onClose,
    disabled: !open || !isTopMostPopover,
  });

  const {dropdownBodyAlign, dropdownBodyAlignWithRespectToOpener} =
    useDropdownBodyAlign({dropdownOpenerRef, disableRecalculations: !open});

  const opener = (
    <ContentOpener
      isOpen={open}
      isActive={isActive}
      openerType={openerType}
      data-testid={dataTestId ? `${dataTestId}Opener` : 'opener'}
      onClick={open ? onClose : onOpenerClick}
      ref={dropdownOpenerRef}
      forceGreyBorder={forceContentOpenerGreyBorder}
      forceIconColor={forceContentOpenerIconColor}
      forceBlackBorderHover={forceBlackBorderOnHover}
      disableFocusOnOpenerButton={disableFocusOnOpenerButton}
    >
      {openerContent}
    </ContentOpener>
  );

  const dropdownBody = (
    <Fade mounted={open} duration={animationDuration}>
      <DropdownBody
        data-testid={dataTestId ? `${dataTestId}Body` : 'body'}
        data-center-dropdown-content={centerDropDownContent}
        onClick={closeOnContentClick ? onClose : undefined}
        $bodyAlignment={
          (renderInPortal
            ? dropdownBodyAlignWithRespectToOpener
            : dropdownBodyAlignProp || dropdownBodyAlign) ??
          DropdownBodyAlignment.LEFT
        }
        $elevation={bodyElevation}
        $fullWidthBody={fullWidthBody}
        $dropdownBodyWidth={dropdownBodyWidth}
        $setConstrainedHeight={setConstrainedHeight}
        $bodyMarginTop={bodyMarginTop}
        role="listbox"
        tabIndex={-1}
      >
        <FocusManagement ref={focusManagementRefCallback()} tabIndex={0}>
          {headerContent}
          {headerContent && <HeaderDivider />}
          {bodyContent}
        </FocusManagement>
      </DropdownBody>
    </Fade>
  );

  return (
    <Wrapper
      ref={wrapperRef}
      className={className}
      data-testid={dataTestId || 'dropdown'}
      $removePositionRelative={centerDropDownContent}
    >
      {renderInPortal ? (
        <>
          {opener}
          <RenderInPortal portalId={'dropdown-portal-host'}>
            <div ref={bodyWrapperRef}>{dropdownBody}</div>
          </RenderInPortal>

          <HandleClickOutsideWhenInPortal
            bodyRef={bodyWrapperRef}
            openerRef={dropdownOpenerRef}
            open={open}
            onClose={onClose}
          />
        </>
      ) : (
        <>
          {opener}
          {dropdownBody}

          {open && (
            <HandleClickOutside
              wrapperRef={wrapperRef}
              onClose={open ? onClose : () => void 0}
              options={{disabled: disableClickOutside}}
            />
          )}
        </>
      )}
    </Wrapper>
  );
};

const HandleClickOutsideWhenInPortal: React.FC<{
  bodyRef: React.RefObject<HTMLDivElement | null>;
  openerRef: React.RefObject<HTMLButtonElement | null>;
  open: boolean;
  onClose: VoidFunction;
}> = ({bodyRef, openerRef, open, onClose}) => {
  useOnClickOutsideMultipleTargets(
    [bodyRef, openerRef],
    open ? onClose : () => void 0,
  );

  return null;
};

type BodyAlignmentWithRespectToOpener = {
  top: number;
  left?: number;
  right?: number;
};

const useDropdownBodyAlign = ({
  dropdownOpenerRef,
  disableRecalculations,
}: {
  dropdownOpenerRef: React.RefObject<HTMLButtonElement | null>;
  disableRecalculations: boolean;
}) => {
  const [dropdownBodyAlign, setDropdownBodyAlign] =
    useState<DropdownBodyAlignment | null>(null);

  const [
    dropdownBodyAlignWithRespectToOpener,
    setDropdownBodyAlignWithRespectToOpener,
  ] = useState<BodyAlignmentWithRespectToOpener | null>(null);

  useDimensionsEffect(
    (dimensions, openerRect: DOMRect) => {
      if (disableRecalculations) return;

      const clientInnerWidth = dimensions.width;
      const doesContentFitIntoViewport =
        openerRect && openerRect.x < clientInnerWidth / 2;

      const dropdownBodyAlign = (() => {
        return doesContentFitIntoViewport
          ? DropdownBodyAlignment.LEFT
          : DropdownBodyAlignment.RIGHT;
      })();

      const dropdownBodyAlignWithRespectToOpener = (() => {
        return doesContentFitIntoViewport
          ? {
              top: openerRect.top + openerRect.height,
              left: openerRect.left,
            }
          : {
              top: openerRect.top + openerRect.height,
              right: clientInnerWidth - openerRect.right,
            };
      })();

      setDropdownBodyAlign(dropdownBodyAlign);
      setDropdownBodyAlignWithRespectToOpener(
        dropdownBodyAlignWithRespectToOpener,
      );
    },
    {
      recalculateLayoutBeforeUpdate: () => {
        const defaultDomRect = {
          width: 0,
          height: 0,
          x: 0,
          y: 0,
        } as DOMRect;

        if (disableRecalculations) return defaultDomRect;

        return dropdownOpenerRef.current
          ? dropdownOpenerRef.current.getBoundingClientRect()
          : defaultDomRect;
      },
      priority: 'low',
      disableScrollUpdates: disableRecalculations,
    },
    [dropdownOpenerRef.current, disableRecalculations],
  );

  return {dropdownBodyAlign, dropdownBodyAlignWithRespectToOpener};
};

const Wrapper = styled.div<{$removePositionRelative?: boolean}>`
  display: inline-block;

  position: ${props => (props.$removePositionRelative ? 'unset' : 'relative')};
`;

const DROPDOWN_CONTENT_WIDTH = 280;

export enum DropdownBodyAlignment {
  LEFT = 'Left',
  RIGHT = 'Right',
}

export const DropdownBody = styled.div<{
  $bodyAlignment: DropdownBodyAlignment | BodyAlignmentWithRespectToOpener;
  $elevation: number;
  $fullWidthBody: boolean;
  $bodyMarginTop: number;
  $dropdownBodyWidth?: string;
  $setConstrainedHeight?: boolean;
}>`
  display: flex;
  flex-direction: column;
  position: ${({$bodyAlignment}) =>
    typeof $bodyAlignment === 'object' ? 'fixed' : 'absolute'};
  z-index: 1;
  border-radius: ${RADIUS.Sm};
  box-shadow: 0 10px 15px 0 rgba(0, 0, 0, 0.08), 0 2px 2px 0 rgba(0, 0, 0, 0.05),
    0 -2px 2px 0 rgba(0, 0, 0, 0.02);
  background-color: white;
  margin-top: ${({$bodyMarginTop}) => $bodyMarginTop}px;
  word-break: break-word;
  z-index: ${({$elevation}) => $elevation};

  top: ${({$bodyAlignment}) =>
    typeof $bodyAlignment === 'object' && $bodyAlignment.top !== undefined
      ? `${$bodyAlignment.top}px`
      : 'unset'};

  left: ${({$bodyAlignment}) => {
    if (
      typeof $bodyAlignment === 'object' &&
      $bodyAlignment.left !== undefined
    ) {
      return `${$bodyAlignment.left}px`;
    }

    if ($bodyAlignment === DropdownBodyAlignment.LEFT) {
      return 0;
    }

    return 'unset';
  }};

  right: ${({$bodyAlignment}) => {
    if (
      typeof $bodyAlignment === 'object' &&
      $bodyAlignment.right !== undefined
    ) {
      return `${$bodyAlignment.right}px`;
    }

    if ($bodyAlignment === DropdownBodyAlignment.RIGHT) {
      return 0;
    }

    return 'unset';
  }};

  min-width: ${({$fullWidthBody}) =>
    $fullWidthBody ? '100%' : `${DROPDOWN_CONTENT_WIDTH}px`};

  width: ${({$dropdownBodyWidth}) => $dropdownBodyWidth ?? 'unset'};

  max-height: ${({$setConstrainedHeight}) =>
    $setConstrainedHeight ? '200px' : 'unset'};

  &[data-center-dropdown-content='true'] {
    margin: auto;
    max-height: 100%;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const HeaderDivider = styled(Divider)`
  margin: ${SPACING['Y.Md']} ${SPACING['X.None']};
`;

const FocusManagement = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow: hidden;
`;
